import useLeadsStore from '../../store/LeadsStore';
import _ from 'lodash';
import i18next from 'i18next';
import RequiredFormLabel from '../RequiredFormLabel/RequiredFormLabel';
import Select from 'react-select';
import ValidationError from '../ValidationError/ValidationError';
import { ReactSelectOption } from 'src/types';
import { useTranslation } from 'react-i18next';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaArrowRightLong } from 'react-icons/fa6';
import { Formik } from 'formik';
import { useApiClient } from '../../hooks/useApiClient';
import {
	ClienteEnum,
	LeadsWizardActiveStepEnum,
	RoleEnum,
} from '../../api/models/Enums';
import { useErrorToast } from '../../hooks/useErrorToast';
import { useAuth } from 'src/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { translateSfAccountsTypes } from '../../utils/translations';
import { toast } from 'react-toastify';
import { validationLeadSfAccount } from '../Leads/Validations/validationLeadSfAccount';

const SfAccountsForm = () => {
	const { t } = useTranslation();
	const { userRole, userReferencePartnerList } = useAuth();
	const { setLeadWizardData, leadWizardData, activeLead, setWizardActiveStep } =
		useLeadsStore();
	const [dealer, setDealer] = useState<ReactSelectOption | null>(null);
	const { apiClient, handleApiResponse } = useApiClient();
	const { showError } = useErrorToast();

	const dealerOptions = useMemo(
		() =>
			(userReferencePartnerList || []).map((value) => ({
				label: value,
				value: value,
			})),
		[userReferencePartnerList]
	);

	const initialValues = useMemo(() => {
		let refPartner =
			leadWizardData.account?.reference_partner ||
			activeLead?.Filiale_Dealer_Titolare__c;
		const canPickFirstDealer = [
			RoleEnum.ADMIN,
			RoleEnum.BACKOFFICE,
			RoleEnum.MASTER,
			RoleEnum.SALES,
			RoleEnum.SALES_DEALER,
			RoleEnum.SALES_INTERNAL,
			RoleEnum.AREA_MANAGER,
		].includes(userRole);
		const hasOneDealer = (userReferencePartnerList || []).length === 1;

		if (!refPartner && canPickFirstDealer && hasOneDealer) {
			refPartner = (userReferencePartnerList || [])[0];
		}

		return {
			Partita_IVA__c:
				leadWizardData.account?.Partita_IVA__c ||
				activeLead?.Partita_Iva__c ||
				'',
			Name: leadWizardData.account?.Name || activeLead?.Company || '',
			Codice_Fiscale__c:
				leadWizardData.account?.Codice_Fiscale__c ||
				activeLead?.Codice_Fiscale__c ||
				'',
			FirstName:
				leadWizardData.account?.FirstName || activeLead?.FirstName || '',
			LastName: leadWizardData.account?.LastName || activeLead?.LastName || '',
			Cliente__c:
				leadWizardData.account?.Cliente__c || activeLead?.Cliente__c || '',
			Email__c:
				leadWizardData.account?.Email__c ||
				activeLead?.Email__c ||
				activeLead?.Email ||
				'',
			Phone: leadWizardData.account?.Phone || activeLead?.Phone || '',
			reference_partner: refPartner || '',
			BillingCity:  leadWizardData.account?.BillingCity,
			BillingStreet:  leadWizardData.account?.BillingStreet,
			Flotta_Potenziale_PC__c: leadWizardData.account?.Flotta_Potenziale_PC__c,
			Flotta_Potenziale_LCV__c: leadWizardData.account?.Flotta_Potenziale_LCV__c,
			Flotta_Totale_LCV__c: leadWizardData.account?.Flotta_Totale_LCV__c,
			Flotta_Totale_PC__c: leadWizardData.account?.Flotta_Totale_PC__c,
		};
	}, [leadWizardData, activeLead, userReferencePartnerList, userRole]);

	const populateDealer = useCallback(() => {
		if (dealer) return;

		const dealerName = initialValues.reference_partner;
		if (!dealerName) return;

		const foundDealer = dealerOptions.find(
			(item) => dealerName?.toLowerCase() === item.value?.toLowerCase()
		);
		setDealer(foundDealer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValues, dealerOptions]);

	useEffect(() => populateDealer(), [populateDealer]);

	const updateWizardState = useCallback(
		(values: any) => {
			setLeadWizardData({
				...leadWizardData,
				account: {
					...leadWizardData.account,
					...values,
					reference_partner: dealer?.value,
				},
			});
		},
		[dealer?.value, leadWizardData, setLeadWizardData]
	);

  const getPivaCfDuplicates = async ({ cf, piva }: {cf?: string; piva?: string}) => {
		const promises: any = [];

		if (!!cf) promises.push(apiClient.salesforceAccountController.getAccounts({ cf }));
		if (!!piva) promises.push(apiClient.salesforceAccountController.getAccounts({ piva }))

		const [byCf, byPiva] = await Promise.all(promises);

		const found = (byCf?.data || []).concat(byPiva?.data || []);
    const unique = _.uniqBy(found, (item) => item.Id);
		console.debug('duplicate accounts', unique);
    return unique;
	}

  const handleFormSubmit = async (values: any) => {
    const { Cliente__c, Partita_IVA__c, Codice_Fiscale__c } = values;

		if (!Cliente__c) {
			toast.warn('select_a_client_type');
			return;
		}

		const isBusinessClient = [
			ClienteEnum.Azienda,
			ClienteEnum.Libero_Professionista,
		].includes(Cliente__c);
		const searchParam: string | null = isBusinessClient
			? Partita_IVA__c
			: Codice_Fiscale__c;

    if (!searchParam) {
      toast.warn('vat_number_fiscal_code_required');
      return;
    }
    try {
      const duplicates = await getPivaCfDuplicates({ cf: Codice_Fiscale__c, piva: Partita_IVA__c })

      const existingAccount = duplicates?.find(item => {
        const recordType = item?.RecordType?.Name?.toLowerCase();

        if (Cliente__c === ClienteEnum.Privato) {
          return recordType == 'privato' && (item.Codice_Fiscale__c?.toLowerCase() === searchParam.toLowerCase());
        } else if (Cliente__c === ClienteEnum.Azienda) {
          return recordType == 'azienda' && (item.Partita_IVA__c?.toLowerCase() === searchParam.toLowerCase());
        } else if (Cliente__c === ClienteEnum.Libero_Professionista) {
          return ['libero professionista', 'libero_professionista'].includes(recordType)
            && (item.Partita_IVA__c?.toLowerCase() === searchParam.toLowerCase());
        }
      })

      if (duplicates?.length && !existingAccount) {
        throw new Error(i18next.t('existing_account_same_p_iva'))
      }

      console.log('existingAccount: ', existingAccount);
      const updatedData = existingAccount
        ? { ...values, Id: existingAccount.Id }
        : values;
      updateWizardState(updatedData);
      setWizardActiveStep(LeadsWizardActiveStepEnum.OPPORTUNITY);
    } catch (error) {
      console.error('Error fetching account:', error);
      showError(error, i18next.t('error_fetching_account'));
    }
  };

	const { data: clientTypeOptions } = useQuery({
		queryKey: ['client_type_fields'],
		queryFn: async (): Promise<ReactSelectOption[]> => {
			const response = await apiClient.leadsControllerService.getLeadFields();
			const clientTypeOptions = response
				.find((item) => item.name === 'Cliente__c')
				?.picklistValues.filter((item) => item.active)
				.map((item) => {
					return {
						value: item.value,
						label: translateSfAccountsTypes(item.label),
					};
				});
			return clientTypeOptions || [];
		},
		staleTime: 1000 * 60 * 5,
		refetchOnWindowFocus: false,
		retry: false,
		refetchOnMount: false,
	});

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationLeadSfAccount}
      onSubmit={handleFormSubmit}
      enableReinitialize={true}
    >
      {({ values, errors, touched, handleChange, handleSubmit, setFieldValue }) => {
        return (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col sm={6}>
                <Form.Group className="mb-3 mt-3">
                  <RequiredFormLabel
                    label={t('client_type')}
                    required={!values.Cliente__c}
                  />
                  <Select
                    menuPosition="fixed"
                    options={clientTypeOptions}
                    onChange={(selected) => {
                      setFieldValue('Cliente__c', selected ? selected.value : null);
											if (!selected?.value?.toLowerCase()?.includes('azienda')) {
												setFieldValue('Flotta_Potenziale_LCV__c', null)
												setFieldValue('Flotta_Potenziale_PC__c', null)
												setFieldValue('Flotta_Totale_LCV__c', null)
												setFieldValue('Flotta_Totale_PC__c', null)
											}
                    }}
                    value={clientTypeOptions?.find((option) => (values.Cliente__c ?? '') === option.value) || null}
                    className={`custom-select ${touched.Cliente__c && errors.Cliente__c ? 'select-error' : ''}`}
                  />
                  {touched.Cliente__c && errors.Cliente__c ? (
                    <div style={{ color: 'red' }}>{errors.Cliente__c as string}</div>
                  ) : null}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              {values?.Cliente__c === ClienteEnum.Azienda && (
                <>
                  <Col sm={6}>
                    <Form.Group className="mb-3 mt-3">
                      <RequiredFormLabel label={i18next.t('business_fiscal_code')} required={true} />
                      <Form.Control
                        type="text"
                        name="Partita_IVA__c"
                        onChange={handleChange}
                        value={values.Partita_IVA__c}
                        isInvalid={!!errors.Partita_IVA__c && !!touched.Partita_IVA__c}
                      />
                      {touched.Partita_IVA__c && errors.Partita_IVA__c && (
                        <Form.Control.Feedback type="invalid">
                          {errors.Partita_IVA__c as string}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
									<Col sm={6}>
										<Form.Group className="mb-3 mt-3">
											<RequiredFormLabel
												label={i18next.t('fiscal_code')}
												required={false}
											/>
											<Form.Control
												type="text"
												name="Codice_Fiscale__c"
												onChange={handleChange}
												value={values.Codice_Fiscale__c}
												isInvalid={
													!!errors.Codice_Fiscale__c &&
													!!touched.Codice_Fiscale__c
												}
											/>
											{touched.Codice_Fiscale__c &&
												errors.Codice_Fiscale__c && (
													<Form.Control.Feedback type="invalid">
														{errors.Codice_Fiscale__c as string}
													</Form.Control.Feedback>
												)}
										</Form.Group>
									</Col>
                  <Col sm={6}>
                    <Form.Group className="mb-3 mt-3">
                      <RequiredFormLabel label={i18next.t('header')} required={true} />
                      <Form.Control
                        type="text"
                        name="Name"
                        onChange={handleChange}
                        value={values.Name}
                        isInvalid={!!errors.Name && !!touched.Name}
                      />
                      {touched.Name && errors.Name && (
                        <Form.Control.Feedback type="invalid">
                          {errors.Name as string}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                </>
              )}

							{values?.Cliente__c === ClienteEnum.Libero_Professionista && (
								<>
									<Col sm={6}>
										<Form.Group className="mb-3 mt-3">
											<RequiredFormLabel
												label={i18next.t('business_fiscal_code')}
												required={true}
											/>
											<Form.Control
												type="text"
												name="Partita_IVA__c"
												onChange={handleChange}
												value={values.Partita_IVA__c}
												isInvalid={
													!!errors.Partita_IVA__c && !!touched.Partita_IVA__c
												}
											/>
											{touched.Partita_IVA__c && errors.Partita_IVA__c && (
												<Form.Control.Feedback type="invalid">
													{errors.Partita_IVA__c as string}
												</Form.Control.Feedback>
											)}
										</Form.Group>
									</Col>
									<Col sm={6}>
										<Form.Group className="mb-3 mt-3">
											<RequiredFormLabel
												label={i18next.t('fiscal_code')}
												required={false}
											/>
											<Form.Control
												type="text"
												name="Codice_Fiscale__c"
												onChange={handleChange}
												value={values.Codice_Fiscale__c}
												isInvalid={
													!!errors.Codice_Fiscale__c &&
													!!touched.Codice_Fiscale__c
												}
											/>
											{touched.Codice_Fiscale__c &&
												errors.Codice_Fiscale__c && (
													<Form.Control.Feedback type="invalid">
														{errors.Codice_Fiscale__c as string}
													</Form.Control.Feedback>
												)}
										</Form.Group>
									</Col>
									<Col sm={6}>
										<Form.Group className="mb-3 mt-3">
											<RequiredFormLabel
												label={i18next.t('first_name')}
												required={true}
											/>
											<Form.Control
												type="text"
												name="FirstName"
												onChange={handleChange}
												value={values.FirstName}
												isInvalid={!!errors.FirstName && !!touched.FirstName}
											/>
											{touched.FirstName && errors.FirstName && (
												<Form.Control.Feedback type="invalid">
													{errors.FirstName as string}
												</Form.Control.Feedback>
											)}
										</Form.Group>
									</Col>
									<Col sm={6}>
										<Form.Group className="mb-3 mt-3">
											<RequiredFormLabel
												label={i18next.t('last_name')}
												required={true}
											/>
											<Form.Control
												type="text"
												name="LastName"
												onChange={handleChange}
												value={values.LastName}
												isInvalid={!!errors.LastName && !!touched.LastName}
											/>
											{touched.LastName && errors.LastName && (
												<Form.Control.Feedback type="invalid">
													{errors.LastName as string}
												</Form.Control.Feedback>
											)}
										</Form.Group>
									</Col>
								</>
							)}
							{values?.Cliente__c === ClienteEnum.Privato && (
								<>
									<Col sm={6}>
										<Form.Group className="mb-3 mt-3">
											<RequiredFormLabel
												label={i18next.t('fiscal_code')}
												required={true}
											/>
											<Form.Control
												type="text"
												name="Codice_Fiscale__c"
												onChange={handleChange}
												value={values.Codice_Fiscale__c}
												isInvalid={
													!!errors.Codice_Fiscale__c &&
													!!touched.Codice_Fiscale__c
												}
											/>
											{touched.Codice_Fiscale__c &&
												errors.Codice_Fiscale__c && (
													<Form.Control.Feedback type="invalid">
														{errors.Codice_Fiscale__c as string}
													</Form.Control.Feedback>
												)}
										</Form.Group>
									</Col>
									<Col sm={6}>
										<Form.Group className="mb-3 mt-3">
											<RequiredFormLabel
												label={i18next.t('first_name')}
												required={true}
											/>
											<Form.Control
												type="text"
												name="FirstName"
												onChange={handleChange}
												value={values.FirstName}
												isInvalid={!!errors.FirstName && !!touched.FirstName}
											/>
											{touched.FirstName && errors.FirstName && (
												<Form.Control.Feedback type="invalid">
													{errors.FirstName as string}
												</Form.Control.Feedback>
											)}
										</Form.Group>
									</Col>

									<Col sm={6}>
										<Form.Group className="mb-3 mt-3">
											<RequiredFormLabel
												label={i18next.t('last_name')}
												required={true}
											/>
											<Form.Control
												type="text"
												name="LastName"
												onChange={handleChange}
												value={values.LastName}
												isInvalid={!!errors.LastName && !!touched.LastName}
											/>
											{touched.LastName && errors.LastName && (
												<Form.Control.Feedback type="invalid">
													{errors.LastName as string}
												</Form.Control.Feedback>
											)}
										</Form.Group>
									</Col>
								</>
							)}

							<Col sm={6}>
								<Form.Group className="mb-3 mt-3">
									<RequiredFormLabel
										label={i18next.t('phone')}
										required={false}
									/>
									<Form.Control
										type="text"
										name="Phone"
										onChange={handleChange}
										value={values.Phone}
										isInvalid={!!errors.Phone && !!touched.Phone}
									/>
									{touched.Phone && errors.Phone && (
										<Form.Control.Feedback type="invalid">
											{errors.Phone as string}
										</Form.Control.Feedback>
									)}
								</Form.Group>
							</Col>

							<Col sm={6}>
								<Form.Group className="mb-3 mt-3">
									<RequiredFormLabel
										label={i18next.t('email')}
										required={false}
									/>
									<Form.Control
										type="text"
										name="Email__c"
										onChange={(e) => {
											handleChange(e);
										}}
										value={values.Email__c}
										isInvalid={!!errors.Email__c && !!touched.Email__c}
									/>
									{touched.Email__c && errors.Email__c && (
										<Form.Control.Feedback type="invalid">
											{errors.Email__c as string}
										</Form.Control.Feedback>
									)}
								</Form.Group>
							</Col>
							<Col md={6}>
								<Form.Group className="mb-3 mt-3">
									<RequiredFormLabel label={i18next.t('BillingCity')} />
									<Form.Control type="text"
										name='BillingCity'
										required
										onChange={handleChange}
										value={values.BillingCity || ''}
									/>
									<ValidationError {...{ errors, touched, name: 'BillingCity' }} />
								</Form.Group>
							</Col>
							<Col md={6}>
								<Form.Group className="mb-3 mt-3">
									<RequiredFormLabel label={i18next.t('BillingStreet')} />
									<Form.Control type="text"
										name='BillingStreet'
										required
										onChange={handleChange}
										value={values.BillingStreet || ''}
									/>
									<ValidationError {...{ errors, touched, name: 'BillingStreet' }} />
								</Form.Group>
							</Col>
							{values.Cliente__c?.toLowerCase()?.includes('azienda') && (<>
									<Col md={6}>
										<Form.Group className="mb-3 mt-3">
											<RequiredFormLabel label={i18next.t('Flotta_Potenziale_PC__c')} />
											<Form.Control
												type="number"
												step="0.1"
												required
												name='Flotta_Potenziale_PC__c'
												onChange={handleChange}
												value={values.Flotta_Potenziale_PC__c ?? undefined}
											/>
											<ValidationError {...{ errors, touched, name: 'Flotta_Potenziale_PC__c' }} />
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group className="mb-3 mt-3">
											<RequiredFormLabel label={i18next.t('Flotta_Potenziale_LCV__c')} />
											<Form.Control
												type="number"
												step="0.1"
												required
												name='Flotta_Potenziale_LCV__c'
												onChange={handleChange}
												value={values.Flotta_Potenziale_LCV__c ?? undefined}
											/>
											<ValidationError {...{ errors, touched, name: 'Flotta_Potenziale_LCV__c' }} />
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group className="mb-3 mt-3">
											<RequiredFormLabel label={i18next.t('Flotta_Totale_PC__c')} />
											<Form.Control
												type="number"
												step="0.1"
												required
												name='Flotta_Totale_PC__c'
												onChange={handleChange}
												value={values.Flotta_Totale_PC__c ?? undefined}
											/>
											<ValidationError {...{ errors, touched, name: 'Flotta_Totale_PC__c' }} />
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group className="mb-3 mt-3">
											<RequiredFormLabel label={i18next.t('Flotta_Totale_LCV__c')} />
											<Form.Control
												type="number"
												step="0.1"
												required
												name='Flotta_Totale_LCV__c'
												onChange={handleChange}
												value={values.Flotta_Totale_LCV__c ?? undefined}
											/>
											<ValidationError {...{ errors, touched, name: 'Flotta_Totale_LCV__c' }} />
										</Form.Group>
									</Col>
							</>)}

              <Col md={6}>
                <Form.Group className="mb-3 mt-3">
                  <RequiredFormLabel label='Dealer' required={true} />
                  <Select
                    menuPosition="fixed"
                    isClearable
                    name="reference_partner"
                    options={dealerOptions}
                    value={dealer}
                    defaultValue={dealer}
                    onChange={(dealer) => {
                      setFieldValue('reference_partner', dealer?.value || '')
                      setDealer(dealer)
                    }}
                  />
                  <ValidationError {...{ errors, touched, name: 'reference_partner' }} />
                </Form.Group>
              </Col>
            </Row>
            <Col className="d-flex justify-content-end align-items-center">
              <Button
                type="submit"
                style={{ marginTop: '10px' }}
              >
                {t('opportunity')}
                <FaArrowRightLong className="ms-2" />
              </Button>
            </Col>
          </Form>
        )
      }}
    </Formik>
  );
};

export default SfAccountsForm;
